import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Tabs,
  Tab,
  Box,
  Alert
} from '@mui/material';
import ForestIcon from '@mui/icons-material/Forest';

import VerifiedIcon from '@mui/icons-material/Verified';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CarbonTicker from '../components/carbon/CarbonTicker';

interface RegistrationData {
  [key: string]: string;
}

const initialFarm = { name: '', location: '', size: '', contact: '' };
const initialLogistics = { company: '', regNumber: '', contact: '', trucks: '' };
const initialTrader = { name: '', email: '', organization: '' };

const CarbonCredits: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [farmData, setFarmData] = useState<RegistrationData>(initialFarm);
  const [logisticsData, setLogisticsData] = useState<RegistrationData>(initialLogistics);
  const [traderData, setTraderData] = useState<RegistrationData>(initialTrader);
  const [success, setSuccess] = useState<string | null>(null);
  const [errors, setErrors] = useState<RegistrationData>({});
  const [loading, setLoading] = useState(false);

  const validateFarm = () => {
    const errs: RegistrationData = {};
    if (!farmData.name) errs.name = 'Farm name is required';
    if (!farmData.location) errs.location = 'Location is required';
    if (!farmData.size) errs.size = 'Farm size is required';
    if (!farmData.contact) errs.contact = 'Contact is required';
    return errs;
  };

  const validateLogistics = () => {
    const errs: RegistrationData = {};
    if (!logisticsData.company) errs.company = 'Company name is required';
    if (!logisticsData.regNumber) errs.regNumber = 'Registration number is required';
    if (!logisticsData.trucks) errs.trucks = 'Fleet size is required';
    if (!logisticsData.contact) errs.contact = 'Contact is required';
    return errs;
  };

  const validateTrader = () => {
    const errs: RegistrationData = {};
    if (!traderData.name) errs.name = 'Full name is required';
    if (!traderData.email) errs.email = 'Email is required';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(traderData.email)) errs.email = 'Invalid email format';
    if (!traderData.organization) errs.organization = 'Organization is required';
    return errs;
  };

  const handleSubmit = () => {
    let errs: RegistrationData = {};
    if (tab === 0) errs = validateFarm();
    if (tab === 1) errs = validateLogistics();
    if (tab === 2) errs = validateTrader();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setLoading(true);
    setTimeout(() => {
      setSuccess('Registration submitted successfully. We will contact you soon.');
      setLoading(false);
    }, 1200);
  };

  const renderFarmForm = () => (
    <Box component="form" sx={{ mt: 2 }} noValidate>
      <TextField fullWidth label="Farm Name" sx={{ mb: 2 }} value={farmData.name} onChange={e => setFarmData({ ...farmData, name: e.target.value })} error={!!errors.name} helperText={errors.name} required />
      <TextField fullWidth label="Location" sx={{ mb: 2 }} value={farmData.location} onChange={e => setFarmData({ ...farmData, location: e.target.value })} error={!!errors.location} helperText={errors.location} required />
      <TextField fullWidth label="Farm Size (acres)" sx={{ mb: 2 }} value={farmData.size} onChange={e => setFarmData({ ...farmData, size: e.target.value })} error={!!errors.size} helperText={errors.size} required />
      <TextField fullWidth label="Contact" sx={{ mb: 2 }} value={farmData.contact} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFarmData({ ...farmData, contact: e.target.value })} error={!!errors.contact} helperText={errors.contact} required />
      <Button variant="contained" onClick={handleSubmit} disabled={loading}>{loading ? 'Registering...' : 'Register Farm'}</Button>
    </Box>
  );

  const renderLogisticsForm = () => (
    <Box component="form" sx={{ mt: 2 }} noValidate>
      <TextField fullWidth label="Company Name" sx={{ mb: 2 }} value={logisticsData.company} onChange={e => setLogisticsData({ ...logisticsData, company: e.target.value })} error={!!errors.company} helperText={errors.company} required />
      <TextField fullWidth label="Registration Number" sx={{ mb: 2 }} value={logisticsData.regNumber} onChange={e => setLogisticsData({ ...logisticsData, regNumber: e.target.value })} error={!!errors.regNumber} helperText={errors.regNumber} required />
      <TextField fullWidth label="Fleet Size" sx={{ mb: 2 }} value={logisticsData.trucks} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLogisticsData({ ...logisticsData, trucks: e.target.value })} error={!!errors.trucks} helperText={errors.trucks} required />
      <TextField fullWidth label="Contact" sx={{ mb: 2 }} value={logisticsData.contact} onChange={e => setLogisticsData({ ...logisticsData, contact: e.target.value })} error={!!errors.contact} helperText={errors.contact} required />
      <Button variant="contained" onClick={handleSubmit} disabled={loading}>{loading ? 'Registering...' : 'Register Company'}</Button>
    </Box>
  );

  const renderTraderForm = () => (
    <Box component="form" sx={{ mt: 2 }} noValidate>
      <TextField fullWidth label="Full Name" sx={{ mb: 2 }} value={traderData.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTraderData({ ...traderData, name: e.target.value })} error={!!errors.name} helperText={errors.name} required />
      <TextField fullWidth label="Email" sx={{ mb: 2 }} value={traderData.email} onChange={e => setTraderData({ ...traderData, email: e.target.value })} error={!!errors.email} helperText={errors.email} required type="email" />
      <TextField fullWidth label="Organization" sx={{ mb: 2 }} value={traderData.organization} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTraderData({ ...traderData, organization: e.target.value })} error={!!errors.organization} helperText={errors.organization} required />
      <Button variant="contained" onClick={handleSubmit} disabled={loading}>{loading ? 'Registering...' : 'Register Trader'}</Button>
    </Box>
  );

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <CarbonTicker />
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h4" gutterBottom>
          Carbon Credit Marketplace
        </Typography>
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Tabs value={tab} onChange={(event, newValue) => {
            setTab(newValue);
            setSuccess(null);
            setErrors({});
          }} aria-label="registration tabs" sx={{ mb: 3 }}>
          <Tab label="Farm Registration" />
          <Tab label="Logistics Companies" />
          <Tab label="Trader Registration" />
        </Tabs>
        {tab === 0 && renderFarmForm()}
        {tab === 1 && renderLogisticsForm()}
        {tab === 2 && renderTraderForm()}
      </Paper>
    </Container>
  );
};

export default CarbonCredits;
